import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initSimpleZoom(signal);
    // Garante thumbs/slides da cor certa no load (todas as cores ficam no DOM).
    this.#filterImagesByColor(this);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Zoom simplificado leve: translate3d + scale (GPU), rAF, sem mudar transform-origin no pan.
   * No toque, dois toques entram e dois toques saem. Clique do rato continua num toque.
   * @param {AbortSignal} signal
   */
  #initZoomInteractions(signal) {
    const hasSimple = this.dataset.zoomSimple === 'true' || this.classList.contains('media-gallery--simple-zoom');
    const hasDialog = this.dataset.zoomDialog === 'true';
    const bothZooms = this.dataset.bothZooms === 'true' || (hasSimple && hasDialog);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isCoarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    if (!hasSimple && !bothZooms) return;

    const DOUBLE_TAP_MS = 300;
    const TAP_MOVE_PX = 10;
    const PINCH_MAX_MULTIPLIER = 3;
    const baseScale =
      1 + (parseFloat(getComputedStyle(this).getPropertyValue('--simple-zoom-intensity').trim()) || 100) / 100;
    const maxScale = baseScale * PINCH_MAX_MULTIPLIER;

    for (const container of this.querySelectorAll('.product-media-container--simple-zoom')) {
      const image = /** @type {HTMLElement | null} */ (container.querySelector('.product-media__image'));
      if (!image) continue;

      image.style.transformOrigin = '50% 50%';

      let scale = 1;
      let panX = 0;
      let panY = 0;
      let lastTapAt = 0;
      /** @type {ReturnType<typeof setTimeout> | null} */
      let resetClearTimer = null;
      /** @type {{ x: number, y: number } | null} */
      let pointerStart = null;
      let panOriginX = 0;
      let panOriginY = 0;
      let didPan = false;
      let dragging = false;
      let rafId = 0;
      let needsPaint = false;
      let animateNextPaint = false;
      /** @type {{ x: number, y: number } | null} */
      let pendingFollow = null;
      /** @type {{ distance: number, scale: number, panX: number, panY: number } | null} */
      let pinchState = null;
      /** @type {{ left: number, top: number, width: number, height: number, cx: number, cy: number }} */
      let rectCache = { left: 0, top: 0, width: 1, height: 1, cx: 0, cy: 0 };

      const isZoomed = () => scale > 1.02;

      const cacheRect = () => {
        const rect = container.getBoundingClientRect();
        rectCache = {
          left: rect.left,
          top: rect.top,
          width: rect.width || 1,
          height: rect.height || 1,
          cx: rect.left + rect.width / 2,
          cy: rect.top + rect.height / 2,
        };
      };

      const clampPan = () => {
        const maxX = Math.max(0, ((scale - 1) * rectCache.width) / 2);
        const maxY = Math.max(0, ((scale - 1) * rectCache.height) / 2);
        panX = Math.min(maxX, Math.max(-maxX, panX));
        panY = Math.min(maxY, Math.max(-maxY, panY));
      };

      const paint = () => {
        rafId = 0;
        if (!needsPaint && !pendingFollow) return;
        needsPaint = false;

        if (pendingFollow && isZoomed() && !dragging && !pinchState) {
          cacheRect();
          panFromPointer(pendingFollow.x, pendingFollow.y);
          pendingFollow = null;
        }

        if (animateNextPaint && !prefersReducedMotion) {
          image.style.transition = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
        } else {
          image.style.transition = 'none';
        }
        animateNextPaint = false;

        image.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
      };

      /**
       * @param {boolean} [animate]
       */
      const schedulePaint = (animate = false) => {
        if (animate) animateNextPaint = true;
        needsPaint = true;
        if (!rafId) rafId = requestAnimationFrame(paint);
      };

      /**
       * Mantém o ponto sob o cursor/dedo ao mudar a escala.
       * @param {number} clientX
       * @param {number} clientY
       * @param {number} nextScale
       */
      const scaleAtPoint = (clientX, clientY, nextScale) => {
        const prev = scale < 1.001 ? 1 : scale;
        const next = Math.min(maxScale, Math.max(1, nextScale));
        const ox = clientX - rectCache.cx;
        const oy = clientY - rectCache.cy;
        const ratio = next / prev;
        panX = ox - (ox - panX) * ratio;
        panY = oy - (oy - panY) * ratio;
        scale = next;
        clampPan();
      };

      /**
       * Desktop: posição do rato mapeia o pan (sem drag).
       * @param {number} clientX
       * @param {number} clientY
       */
      const panFromPointer = (clientX, clientY) => {
        const xRatio = (clientX - rectCache.left) / rectCache.width;
        const yRatio = (clientY - rectCache.top) / rectCache.height;
        panX = (0.5 - xRatio) * rectCache.width * (scale - 1);
        panY = (0.5 - yRatio) * rectCache.height * (scale - 1);
        clampPan();
      };

      const setZoomedUi = (active) => {
        image.classList.toggle('is-simple-zoom-active', active);
        container.classList.toggle('is-simple-zoomed', active);
      };

      const clearZoom = () => {
        scale = 1;
        panX = 0;
        panY = 0;
        pinchState = null;
        dragging = false;
        didPan = false;
        image.style.transition = 'none';
        image.style.transform = '';
        setZoomedUi(false);
      };

      /**
       * @param {number} clientX
       * @param {number} clientY
       */
      const zoomInAt = (clientX, clientY) => {
        if (resetClearTimer) {
          clearTimeout(resetClearTimer);
          resetClearTimer = null;
        }
        cacheRect();
        scale = 1;
        panX = 0;
        panY = 0;
        scaleAtPoint(clientX, clientY, baseScale);
        setZoomedUi(true);
        schedulePaint(true);
      };

      const zoomOutAt = () => {
        scale = 1;
        panX = 0;
        panY = 0;
        schedulePaint(true);

        const finish = () => {
          image.removeEventListener('transitionend', onEnd);
          if (resetClearTimer) {
            clearTimeout(resetClearTimer);
            resetClearTimer = null;
          }
          clearZoom();
        };

        const onEnd = (event) => {
          if (event && event.target !== image) return;
          if (event && 'propertyName' in event && event.propertyName !== 'transform') return;
          finish();
        };

        if (prefersReducedMotion) {
          clearZoom();
          return;
        }

        image.addEventListener('transitionend', onEnd);
        resetClearTimer = setTimeout(finish, 320);
      };

      /**
       * @param {number} clientX
       * @param {number} clientY
       */
      const toggleSimpleZoom = (clientX, clientY) => {
        if (isZoomed()) zoomOutAt();
        else zoomInAt(clientX, clientY);
      };

      /**
       * @param {TouchList} touches
       */
      const pinchDistance = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
      };

      container.addEventListener(
        'pointerdown',
        (event) => {
          if (!(event.target instanceof Element)) return;
          if (event.target.closest('[data-zoom-icon], .product-media-zoom-icon')) return;

          pointerStart = { x: event.clientX, y: event.clientY };
          didPan = false;
          cacheRect();

          if (!isZoomed()) return;

          dragging = true;
          panOriginX = panX;
          panOriginY = panY;
          event.stopPropagation();

          if (event.pointerType !== 'touch') {
            try {
              container.setPointerCapture(event.pointerId);
            } catch {
              /* ignore */
            }
          }
        },
        { signal }
      );

      container.addEventListener(
        'pointermove',
        (event) => {
          if (!isZoomed() || pinchState) return;

          // Desktop hover-follow: só guarda o ponto; o rAF faz o resto
          if (!isCoarsePointer && event.pointerType !== 'touch' && event.buttons === 0 && !dragging) {
            pendingFollow = { x: event.clientX, y: event.clientY };
            if (!rafId) rafId = requestAnimationFrame(paint);
            return;
          }

          if (!dragging || !pointerStart) return;

          const dx = event.clientX - pointerStart.x;
          const dy = event.clientY - pointerStart.y;
          if (!didPan && Math.hypot(dx, dy) < TAP_MOVE_PX) return;

          didPan = true;
          panX = panOriginX + dx;
          panY = panOriginY + dy;
          clampPan();
          schedulePaint(false);
          event.preventDefault();
          event.stopPropagation();
        },
        { signal, passive: false }
      );

      container.addEventListener(
        'pointerup',
        (event) => {
          if (!(event.target instanceof Element)) return;
          if (event.target.closest('[data-zoom-icon], .product-media-zoom-icon')) return;

          const moved = didPan;
          dragging = false;

          if (pinchState) {
            pointerStart = null;
            return;
          }
          if (!pointerStart) return;

          const { clientX, clientY } = event;
          const tapMoved =
            moved ||
            Math.abs(clientX - pointerStart.x) > TAP_MOVE_PX ||
            Math.abs(clientY - pointerStart.y) > TAP_MOVE_PX;
          pointerStart = null;

          if (tapMoved) return;

          const isTouch = isCoarsePointer || event.pointerType === 'touch';

          if (isTouch) {
            const now = Date.now();
            if (now - lastTapAt < DOUBLE_TAP_MS) {
              lastTapAt = 0;
              toggleSimpleZoom(clientX, clientY);
              return;
            }

            lastTapAt = now;
            return;
          }

          toggleSimpleZoom(clientX, clientY);
        },
        { signal }
      );

      container.addEventListener(
        'pointercancel',
        () => {
          pointerStart = null;
          dragging = false;
          pinchState = null;
        },
        { signal }
      );

      container.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 2) return;
          lastTapAt = 0;
          pointerStart = null;
          dragging = false;
          cacheRect();
          const dist = pinchDistance(event.touches);
          if (dist <= 0) return;

          if (!isZoomed()) {
            scale = baseScale;
            panX = 0;
            panY = 0;
            setZoomedUi(true);
            schedulePaint(false);
          }

          pinchState = { distance: dist, scale, panX, panY };
          didPan = true;
          event.preventDefault();
          event.stopPropagation();
        },
        { signal, passive: false }
      );

      container.addEventListener(
        'touchmove',
        (event) => {
          if (event.touches.length === 2 && pinchState && pinchState.distance > 0) {
            const dist = pinchDistance(event.touches);
            const midX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
            const midY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
            scale = pinchState.scale;
            panX = pinchState.panX;
            panY = pinchState.panY;
            scaleAtPoint(midX, midY, pinchState.scale * (dist / pinchState.distance));
            schedulePaint(false);
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          if (isZoomed() && event.touches.length === 1) {
            event.stopPropagation();
          }
        },
        { signal, passive: false }
      );

      container.addEventListener(
        'touchend',
        (event) => {
          if (event.touches.length >= 2) return;
          if (!pinchState) return;

          const ended = pinchState;
          pinchState = null;
          if (scale <= 1.05) {
            zoomOutAt();
            return;
          }
          // Mantém escala da pinça
          void ended;
        },
        { signal }
      );

      signal.addEventListener('abort', () => {
        if (rafId) cancelAnimationFrame(rafId);
        if (resetClearTimer) clearTimeout(resetClearTimer);
      });
    }
  }

  /**
   * @deprecated Mantido como alias — zoom passa a ser por clique/toque.
   * @param {AbortSignal} signal
   */
  #initSimpleZoom(signal) {
    this.#initZoomInteractions(signal);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Pré-carrega as imagens antes de trocar o DOM para aparecerem juntas (padrão Solo).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Com filtro por cor: se a galeria atual já tem os media-ids da nova, só filtra.
    if (this.dataset.filterByColor === 'true' && this.#canFilterInPlace(newMediaGallery)) {
      this.#filterImagesByColor(this);
      this.#selectFeaturedFromGallery(newMediaGallery);
      return;
    }

    await this.#preloadGalleryImages(newMediaGallery);
    this.#eagerFirstImages(newMediaGallery);

    // Replace the element
    this.replaceWith(newMediaGallery);

    // If the new gallery has animation classes, ensure it's visible immediately
    // (since it's already on screen when variant changes)
    if (newMediaGallery.classList.contains('media-gallery--animated')) {
      // Force immediate visibility for variant updates
      newMediaGallery.classList.add('is-visible');
      
      // Also reinitialize animation system if needed
      const animationSelector = newMediaGallery.className.match(/media-gallery-animated-[\w-]+/);
      if (animationSelector && window.AnimationSystem) {
        setTimeout(() => {
          window.AnimationSystem.init(`.${animationSelector[0]}`, {
            animatedClass: 'media-gallery--animated'
          });
        }, 10);
      }
    }

    // Reinitialize cascade animation for thumbnails if needed
    this.#reinitializeCascadeAnimation(newMediaGallery);

    // Reinitialize thumbnail navigation arrows if needed
    this.#reinitializeThumbnailNavigation(newMediaGallery);

    // Filter images by color if enabled
    this.#filterImagesByColor(newMediaGallery);
  };

  /**
   * @param {HTMLElement} newGallery
   */
  #canFilterInPlace(newGallery) {
    const currentIds = new Set(
      [...this.querySelectorAll('slideshow-slide[slide-id], .carousel-extended__slide[slide-id]')]
        .map((el) => el.getAttribute('slide-id'))
        .filter(Boolean)
    );
    if (currentIds.size === 0) return false;

    const newSlides = [...newGallery.querySelectorAll('slideshow-slide[slide-id], .carousel-extended__slide[slide-id]')];
    if (newSlides.length === 0) return false;

    return newSlides.every((slide) => currentIds.has(slide.getAttribute('slide-id')));
  }

  /**
   * @param {HTMLElement} newGallery
   */
  #selectFeaturedFromGallery(newGallery) {
    const featured =
      newGallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
      newGallery.querySelector('slideshow-slide');
    const featuredId = featured?.getAttribute('slide-id');
    if (!featuredId || typeof this.slideshow?.select !== 'function') return;

    const slides = [...(this.slideshow.refs?.slides ?? this.querySelectorAll('slideshow-slide'))];
    const index = slides.findIndex((slide) => slide.getAttribute('slide-id') === featuredId);
    if (index >= 0) {
      this.slideshow.select(index, undefined, { animate: false });
    }
  }

  /**
   * @param {HTMLElement} gallery
   */
  #preloadGalleryImages(gallery) {
    const imgs = [...gallery.querySelectorAll('img')];
    return Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            const src = img.getAttribute('src') || '';
            if (!src) {
              resolve();
              return;
            }
            const pre = new Image();
            pre.onload = () => resolve();
            pre.onerror = () => resolve();
            const srcset = img.getAttribute('srcset');
            const sizes = img.getAttribute('sizes');
            if (srcset) pre.srcset = srcset;
            if (sizes) pre.sizes = sizes;
            pre.src = src;
          })
      )
    );
  }

  /**
   * @param {HTMLElement} gallery
   */
  #eagerFirstImages(gallery) {
    [...gallery.querySelectorAll('img')].slice(0, 4).forEach((img) => {
      img.loading = 'eager';
      img.fetchPriority = 'high';
    });
  }

  /**
   * Reinitializes cascade animation for thumbnails after variant update.
   * @param {HTMLElement} gallery - The new media gallery element.
   */
  #reinitializeCascadeAnimation(gallery) {
    // Check if cascade animation is enabled via data attribute
    const cascadeEnabled = gallery.dataset.cascadeEnabled === 'true';
    if (!cascadeEnabled) return;

    // Check if there are thumbnails that need cascade animation
    const thumbnails = gallery.querySelectorAll('.slideshow-control');
    if (thumbnails.length === 0) return;

    // Get cascade animation settings from data attributes
    const cascadeConfig = {
      cascadeType: gallery.dataset.cascadeType || 'fade-in',
      cascadeDuration: parseInt(gallery.dataset.cascadeDuration, 10) || 600,
      cascadeInitialDelay: parseInt(gallery.dataset.cascadeInitialDelay, 10) || 500,
      cascadeStagger: parseInt(gallery.dataset.cascadeStagger, 10) || 200
    };

    // Reset thumbnails
    thumbnails.forEach(thumb => {
      thumb.classList.remove('thumbnail-cascade-visible');
      thumb.style.animationDelay = '';
      thumb.style.opacity = '0';
    });

    // Get visible thumbnails
    const visibleThumbnails = Array.from(thumbnails).filter(thumb => {
      const thumbStyles = window.getComputedStyle(thumb);
      if (thumbStyles.display === 'none' || thumbStyles.visibility === 'hidden') {
        return false;
      }
      
      let parent = thumb.parentElement;
      while (parent && parent !== gallery) {
        const styles = window.getComputedStyle(parent);
        if (styles.display === 'none') {
          return false;
        }
        parent = parent.parentElement;
      }
      
      return true;
    });

    if (visibleThumbnails.length === 0) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      visibleThumbnails.forEach(thumb => {
        thumb.style.opacity = '1';
      });
      return;
    }

    // Animate thumbnails with cascade effect
    visibleThumbnails.forEach((thumb, index) => {
      const delay = cascadeConfig.cascadeInitialDelay + (index * cascadeConfig.cascadeStagger);
      thumb.style.animationDelay = delay + 'ms';
      
      setTimeout(() => {
        thumb.classList.add('thumbnail-cascade-visible');
      }, 10);
    });
  }

  /**
   * Reinitializes thumbnail navigation arrows after variant update.
   * @param {HTMLElement} gallery - The new media gallery element.
   */
  #reinitializeThumbnailNavigation(gallery) {
    // Check if thumbnails are at bottom position (where navigation arrows are used)
    const galleryId = gallery.dataset.galleryId;
    if (!galleryId) return;

    const navContainer = gallery.querySelector(`.media-gallery-${galleryId}-thumbnails-nav`);
    if (!navContainer) return;

    const prevArrow = navContainer.querySelector('.thumbnail-nav-arrow--prev');
    const nextArrow = navContainer.querySelector('.thumbnail-nav-arrow--next');
    const thumbnailsContainer = navContainer.querySelector('.slideshow-controls__thumbnails-container');

    if (!prevArrow || !nextArrow || !thumbnailsContainer) return;

    // Remove old event listeners by cloning elements (clean slate)
    const newPrevArrow = prevArrow.cloneNode(true);
    const newNextArrow = nextArrow.cloneNode(true);
    prevArrow.replaceWith(newPrevArrow);
    nextArrow.replaceWith(newNextArrow);

    // Get settings from data attributes or use defaults
    const thumbnailWidth = parseInt(gallery.style.getPropertyValue('--thumbnail-width'), 10) || 65;
    const visibleCount = 5; // Default, could be made configurable

    function updateArrowVisibility() {
      const scrollLeft = thumbnailsContainer.scrollLeft;
      const scrollWidth = thumbnailsContainer.scrollWidth;
      const clientWidth = thumbnailsContainer.clientWidth;
      const scrollRight = scrollWidth - scrollLeft - clientWidth;

      // Show/hide prev arrow
      if (scrollLeft <= 1) {
        newPrevArrow.classList.add('hidden');
      } else {
        newPrevArrow.classList.remove('hidden');
      }

      // Show/hide next arrow
      if (scrollRight <= 1) {
        newNextArrow.classList.add('hidden');
      } else {
        newNextArrow.classList.remove('hidden');
      }
    }

    function scrollThumbnails(direction) {
      const thumbnailWidthWithGap = thumbnailWidth + 8; // width + gap
      const scrollAmount = thumbnailWidthWithGap * visibleCount;

      const currentScroll = thumbnailsContainer.scrollLeft;
      const targetScroll = direction === 'prev'
        ? Math.max(0, currentScroll - scrollAmount)
        : Math.min(thumbnailsContainer.scrollWidth - thumbnailsContainer.clientWidth, currentScroll + scrollAmount);

      thumbnailsContainer.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }

    // Add event listeners
    newPrevArrow.addEventListener('click', () => scrollThumbnails('prev'));
    newNextArrow.addEventListener('click', () => scrollThumbnails('next'));
    thumbnailsContainer.addEventListener('scroll', updateArrowVisibility);

    // Initial check
    setTimeout(() => {
      updateArrowVisibility();
    }, 100);
  }

  /**
   * Filters images by color based on selected variant color option.
   * Shows only media whose alt text or filename contains the selected color
   * (e.g. selecting "preto" keeps "ciclista-flashback-preto-1").
   * @param {HTMLElement} gallery - The new media gallery element.
   */
  #filterImagesByColor(gallery) {
    const filterByColor = gallery.dataset.filterByColor === 'true';
    if (!filterByColor) return;

    const colorSource = gallery.dataset.filterColorSource || 'alt';

    const variantPicker = gallery.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return;

    const colorOption = Array.from(variantPicker.querySelectorAll('fieldset')).find((fieldset) => {
      const legend = fieldset.querySelector('legend');
      if (!legend) return false;
      const optionName = legend.textContent?.toLowerCase() || '';
      return optionName.includes('cor') || optionName.includes('color');
    });

    if (!colorOption) return;

    const selectedInput = colorOption.querySelector('input:checked, input[checked]');
    if (!selectedInput) return;

    const normalize = (text, { forFilename = false } = {}) => {
      let value = (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\.(jpe?g|png|webp|gif|avif)\b/gi, '');
      if (forFilename) {
        value = value.replace(/[/\\]+/g, ' ');
      }
      return value
        .replace(/[-_.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const useFilename = colorSource === 'filename';
    const selectedColor = normalize(selectedInput.value, { forFilename: useFilename });
    if (!selectedColor) return;

    const getFilename = (src) => (src || '').split('?')[0].split('/').pop() || '';
    const colorNeedle = ` ${selectedColor} `;
    const matchesColor = (text) => ` ${normalize(text, { forFilename: useFilename })} `.includes(colorNeedle);

    const setVisible = (element, visible) => {
      // Limpa inline do Liquid (display:none !important) e toggles de cor
      element.style.removeProperty('display');
      element.classList.toggle('slideshow-controls__thumbnail--color-hidden', !visible);
      element.classList.toggle('dialog-thumbnails-list__thumbnail--color-hidden', !visible);
      if (visible) {
        element.removeAttribute('hidden');
        if (element instanceof HTMLElement) element.hidden = false;
      } else {
        element.style.display = 'none';
        element.setAttribute('hidden', 'true');
        if (element instanceof HTMLElement) element.hidden = true;
      }
    };

    const allMediaElements = gallery.querySelectorAll(
      'slideshow-slide[data-media-color], li[data-media-color], .carousel-extended__slide[data-media-color]'
    );
    allMediaElements.forEach((mediaElement) =>
      setVisible(mediaElement, matchesColor(mediaElement.dataset.mediaColor))
    );

    const thumbnails = gallery.querySelectorAll('.slideshow-control');
    thumbnails.forEach((thumb) => {
      const thumbImg = thumb.querySelector('img');
      if (!thumbImg) return;

      const thumbAlt = thumbImg.getAttribute('alt') || '';
      let thumbText = thumbAlt;
      if (useFilename) {
        thumbText = getFilename(thumbImg.getAttribute('src'));
        // Vídeo/3D: Shopify não expõe o nome original; paridade com media-color-source.liquid
        if (thumb.querySelector('.slideshow-controls__thumbnail-badge')) thumbText += ` ${thumbAlt}`;
      }
      setVisible(thumb, matchesColor(thumbText));
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
